<template>
  <div class="link-analysis-container">
    <CCard>
      <CCardHeader>ابزار تحلیل لینک‌سازی</CCardHeader>
      <CCardBody>
        <CAlert color="info" v-if="loading">
          <CSpinner component="span" size="sm" aria-hidden="true" />
          در حال آنالیز لینک‌ها...
        </CAlert>

        <!-- انتخاب نوع آنالیز -->
        <CRow class="mb-3">
          <CCol md="6">
            <CSelect
                label="نوع آنالیز"
                :options="analysisTypeOptions"
                v-model="analysisType"
            />
          </CCol>
          <CCol md="6">
            <CButton
                color="primary"
                @click="analyzeAll"
                :disabled="loading"
            >
              آنالیز همه
            </CButton>
          </CCol>
        </CRow>

        <!-- نتایج آنالیز -->
        <CTabs v-if="results.length > 0">
          <CTab title="خلاصه نتایج" active>
            <CCard class="mt-3">
              <CCardBody>
                <CRow>
                  <CCol md="3">
                    <CCard class="text-center bg-light">
                      <CCardBody>
                        <div class="text-value-lg">{{ totalProductLinks }}</div>
                        <div>لینک به محصولات</div>
                      </CCardBody>
                    </CCard>
                  </CCol>
                  <CCol md="3">
                    <CCard class="text-center bg-light">
                      <CCardBody>
                        <div class="text-value-lg">{{ totalArticleLinks }}</div>
                        <div>لینک به مقالات</div>
                      </CCardBody>
                    </CCard>
                  </CCol>
                  <CCol md="3">
                    <CCard class="text-center bg-light">
                      <CCardBody>
                        <div class="text-value-lg">{{ totalErrors }}</div>
                        <div>خطاهای لینک‌دهی</div>
                      </CCardBody>
                    </CCard>
                  </CCol>
                  <CCol md="3">
                    <CCard class="text-center bg-light">
                      <CCardBody>
                        <div class="text-value-lg">{{ analyzedItems }}</div>
                        <div>تعداد آنالیز شده</div>
                      </CCardBody>
                    </CCard>
                  </CCol>
                </CRow>
              </CCardBody>
            </CCard>
          </CTab>

          <CTab title="گزارش کلمات کلیدی">
            <CDataTable
                :items="keywordReport"
                :fields="keywordFields"
                items-per-page-select
                :items-per-page="10"
                hover
                pagination
                class="mt-3"
            >
              <template #count="{item}">
                <CBadge color="primary">{{ item.count }}</CBadge>
              </template>
              <template #type="{item}">
                <CBadge :color="item.type === 'product' ? 'info' : 'success'">
                  {{ item.type === 'product' ? 'محصول' : 'مقاله' }}
                </CBadge>
              </template>
            </CDataTable>
          </CTab>

          <CTab title="خطاها">
            <CDataTable
                :items="allErrors"
                :fields="errorFields"
                items-per-page-select
                :items-per-page="10"
                hover
                pagination
                class="mt-3"
            >
              <template #message="{item}">
                <CBadge color="warning">{{ item.message }}</CBadge>
              </template>
            </CDataTable>
          </CTab>
        </CTabs>

        <!-- نتایج جزئیات -->
        <div v-for="(result, index) in results" :key="index" class="mt-4">
          <CCard>
            <CCardHeader>
              <strong>{{ result.title }}</strong>
              <CBadge :color="result.type === 'product' ? 'info' : 'success'" class="mr-2">
                {{ result.type === 'product' ? 'محصول' : 'مقاله' }}
              </CBadge>
              <CBadge color="secondary">{{ result.url }}</CBadge>
            </CCardHeader>
            <CCardBody>
              <CRow>
                <CCol md="3">
                  <strong>لینک به محصولات:</strong> {{ result.productLinksCount }}
                </CCol>
                <CCol md="3">
                  <strong>لینک به مقالات:</strong> {{ result.articleLinksCount }}
                </CCol>
                <CCol md="3">
                  <strong>کلمه کلیدی:</strong> {{ result.keyword }}
                </CCol>
                <CCol md="3">
                  <strong>تعداد کلمات کلیدی:</strong> {{ result.keywordWordCount }}
                </CCol>
              </CRow>

              <div v-if="result.errors && result.errors.length">
                <h6 class="mt-3 text-danger">خطاها:</h6>
                <CAlert color="warning" v-for="(error, i) in result.errors" :key="i">
                  {{ error }}
                </CAlert>
              </div>
            </CCardBody>
          </CCard>
        </div>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
export default {
  name: 'LinkAnalysis',
  props: {
    // لیست مقالات و محصولات برای آنالیز
    items: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      loading: false,
      analysisType: 'all',
      analysisTypeOptions: [
        { value: 'all', label: 'همه موارد' },
        { value: 'articles', label: 'فقط مقالات' },
        { value: 'products', label: 'فقط محصولات' }
      ],
      results: [],
      keywordFields: [
        { key: 'keyword', label: 'کلمه کلیدی' },
        { key: 'count', label: 'تعداد لینک‌ها' },
        { key: 'type', label: 'نوع صفحه' }
      ],
      errorFields: [
        { key: 'title', label: 'صفحه' },
        { key: 'message', label: 'پیغام خطا' }
      ]
    };
  },
  computed: {
    totalProductLinks() {
      return this.results.reduce((sum, result) => sum + result.productLinksCount, 0);
    },
    totalArticleLinks() {
      return this.results.reduce((sum, result) => sum + result.articleLinksCount, 0);
    },
    totalErrors() {
      return this.results.reduce((sum, result) => sum + (result.errors ? result.errors.length : 0), 0);
    },
    analyzedItems() {
      return this.results.length;
    },
    keywordReport() {
      const report = {};

      this.results.forEach(result => {
        if (result.linkedKeywords) {
          result.linkedKeywords.forEach(link => {
            const key = `${link.keyword}-${link.type}`;
            if (!report[key]) {
              report[key] = {
                keyword: link.keyword,
                type: link.type,
                count: 0
              };
            }
            report[key].count += link.count;
          });
        }
      });

      return Object.values(report);
    },
    allErrors() {
      const errors = [];
      this.results.forEach(result => {
        if (result.errors && result.errors.length) {
          result.errors.forEach(error => {
            errors.push({
              title: result.title,
              message: error
            });
          });
        }
      });
      return errors;
    }
  },
  methods: {
    async analyzeAll() {
      this.loading = true;
      this.results = [];

      try {
        // فیلتر کردن موارد بر اساس نوع انتخابی
        let itemsToAnalyze = this.items;
        if (this.analysisType === 'articles') {
          itemsToAnalyze = this.items.filter(item => item.type === 'article');
        } else if (this.analysisType === 'products') {
          itemsToAnalyze = this.items.filter(item => item.type === 'product');
        }

        // آنالیز هر آیتم
        for (const item of itemsToAnalyze) {
          const result = await this.analyzeItem(item);
          this.results.push(result);
        }
      } catch (error) {
        console.error('Error in analysis:', error);
      } finally {
        this.loading = false;
      }
    },

    analyzeItem(item) {
      return new Promise((resolve) => {
        // شبیه‌سازی پردازش غیرهمزمان
        setTimeout(() => {
          const result = {
            title: item.title,
            url: item.url,
            type: item.type,
            keyword: item.keyword,
            keywordWordCount: item.keyword ? item.keyword.split(' ').length : 0,
            productLinksCount: 0,
            articleLinksCount: 0,
            errors: [],
            linkedKeywords: []
          };

          // ایجاد یک DOM موقت برای تجزیه HTML
          const parser = new DOMParser();
          const doc = parser.parseFromString(item.htmlContent, 'text/html');

          // یافتن تمام لینک‌ها
          const links = doc.querySelectorAll('a');

          links.forEach(link => {
            const href = link.getAttribute('href');

            // بررسی لینک‌های داخلی
            if (href && href.includes('/')) {
              if (href.includes('/product')) {
                result.productLinksCount++;

                // استخراج کلمه کلیدی از anchor text
                const anchorText = link.textContent.trim();
                if (anchorText) {
                  this.trackKeyword(result, anchorText, 'product');
                }
              }
              else if (href.includes('/blog')) {
                result.articleLinksCount++;

                // استخراج کلمه کلیدی از anchor text
                const anchorText = link.textContent.trim();
                if (anchorText) {
                  this.trackKeyword(result, anchorText, 'article');
                }

                // بررسی تطابق تعداد کلمات کلیدی
                if (item.keyword && anchorText) {
                  const itemKeywordCount = item.keyword.split(' ').length;
                  const anchorKeywordCount = anchorText.split(' ').length;

                  if (Math.abs(itemKeywordCount - anchorKeywordCount) > 1) {
                    result.errors.push(
                        `تعداد کلمات کلیدی در لینک به مقاله (${anchorKeywordCount}) با صفحه مبدأ (${itemKeywordCount}) همخوانی ندارد`
                    );
                  }
                }
              }
            }
          });

          resolve(result);
        }, 100); // تأخیر شبیه‌سازی شده برای پردازش
      });
    },

    trackKeyword(result, keyword, type) {
      const existing = result.linkedKeywords.find(k => k.keyword === keyword && k.type === type);
      if (existing) {
        existing.count++;
      } else {
        result.linkedKeywords.push({
          keyword,
          type,
          count: 1
        });
      }
    }
  }
};
</script>

<style scoped>
.link-analysis-container {
  direction: rtl;
  text-align: right;
}
</style>